import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { ThemeProvider } from 'styled-components';
import Sticky from 'react-stickynode';
import { DrawerProvider } from 'common/src/contexts/DrawerContext';
import { saasModernTheme } from 'common/src/theme/saasModern';
import { ResetCSS } from 'common/src/assets/css/style';
import { GlobalStyle, ContentWrapper } from '../containers/sassModern.style';

import BannerSection from '../containers/Banner';
import Navbar from '../containers/Navbar';
import WorkingProcessSection from '../containers/WorkingProcess';
import TextBoxSection from '../containers/TextBox';
import InfoSection from '../containers/Info';
import ComparisonTable from '../containers/ComparisonTable';
import Footer from '../containers/Footer';
import SEO from '../components/seo';
import FaqSection from '../containers/Faq';
import { useTranslation } from 'react-i18next';

const GoogleAndroidPasswordManager = ({ title, description, pageContext }) => {
  const { t } = useTranslation();

  const androidFeatures = [
    {
      title: 'Native Android Integration',
      alt: 'Native Android Integration',
      description:
        "Seamlessly integrates with Android's autofill framework. Works natively with Android 8.0 (Oreo) and above for secure password autofill across all apps.",
    },
    {
      title: 'Google Play Store Available',
      alt: 'Google Play Store Available',
      description:
        "Download directly from the Google Play Store with regular updates. Built to meet Google's strict security and privacy requirements for Android applications.",
    },
    {
      title: 'Biometric Authentication',
      alt: 'Biometric Authentication',
      description:
        "Unlock your password vault using fingerprint, face recognition, or iris scanning. Enhanced security with Android's built-in biometric authentication framework.",
    },
    {
      title: 'Offline Mode Support',
      alt: 'Offline Mode Support',
      description:
        'Access your passwords even without internet connection. Local encrypted cache ensures your credentials are always available when you need them.',
    },
    {
      title: 'Cross-Device Android Sync',
      alt: 'Cross-Device Android Sync',
      description:
        'Sync passwords across all your Android devices—phones, tablets, Android TV, and Chromebooks. Your vault stays updated in real-time across the entire Android ecosystem.',
    },
    {
      title: 'Open Source Security',
      alt: 'Open Source Security',
      description:
        'Android users value transparency—Psono delivers. Fully auditable code reviewed by security experts. No hidden functionality, no backdoors, complete privacy.',
    },
  ];

  const androidFaqData = [
    {
      title: 'Is Psono a good password manager for Android?',
      description:
        'Psono provides Android users with professional password management at zero cost. The app is built on auditable open-source code, allowing security experts to verify its implementation. Organizations can self-host for complete data control. With NaCl encryption, seamless multi-device sync, and comprehensive free features, Psono offers a compelling alternative to subscription-based password managers for Android users.',
      expend: false,
    },
    {
      title: 'How do I install Psono on Android?',
      description:
        'Create your Psono account at psono.pw and verify your email. Open the Google Play Store on your Android device and search for "Psono Password Manager". Install the app and launch it. Sign in with your credentials, and Psono will sync your vault. Enable Android Autofill in your device settings to use Psono for automatic password filling across all apps.',
      expend: false,
    },
    {
      title: 'Which password manager works best on Android?',
      description:
        'For Android users seeking robust password management without recurring fees, Psono stands out. The app provides transparent open-source code that undergoes community security review. After installation from Google Play, Psono operates seamlessly—capturing credentials, generating strong passwords, and synchronizing your encrypted vault across all devices automatically.',
      expend: false,
    },
    {
      title: 'Can I export passwords from Psono on Android?',
      description:
        'Yes, Psono ensures complete data ownership through its export feature. While the export function is best accessed through the web interface, you can log into psono.pw from your Android device, navigate to "Other" > "Export", choose your datastore and format (JSON or CSV), and download your password data.',
      expend: false,
    },
    {
      title: 'How do I enable Android Autofill for Psono?',
      description:
        'After installing Psono from Google Play, go to your Android device Settings > System > Languages & input > Advanced > Autofill service. Select Psono as your autofill service. Now when you tap on login fields in apps and websites, Psono will offer to autofill your saved credentials securely.',
      expend: false,
    },
  ];

  const comparisonFeatures = [
    {
      title: 'Advanced Encryption',
      description:
        'Protect passwords with NaCl cryptography. Vault data is encrypted client-side before it is sent to the server.',
      psonoValue: true,
      androidValue: '-',
    },
    {
      title: 'Open Source & Auditable',
      description:
        'Publicly available source code allows security experts worldwide to verify security claims. Regular third-party audits ensure continued protection.',
      psonoValue: true,
      androidValue: '-',
    },
    {
      title: 'Advanced Two-Factor Authentication',
      description:
        'Secure your vault with TOTP authenticators, hardware security keys, or FIDO2 WebAuthn credentials for enhanced protection.',
      psonoValue: true,
      androidValue: '-',
    },
    {
      title: 'Password Generator',
      description:
        'Create long, complex, and unique passwords for every site with customizable options for maximum security.',
      psonoValue: true,
      androidValue: '-',
    },
    {
      title: 'Self-Hosting Option',
      description:
        'Deploy on your own infrastructure for complete control over your data and compliance with regulatory requirements.',
      psonoValue: true,
      androidValue: '-',
    },
    {
      title: 'Cross-Platform Sync',
      description:
        'Access your passwords on any browser, mobile device, or desktop OS with automatic synchronization across all devices.',
      psonoValue: true,
      androidValue: 'Limited',
    },
  ];

  return (
    <ThemeProvider theme={saasModernTheme}>
      <Fragment>
        <SEO
          title={t('Best Free Password Manager Android | Psono')}
          lang={pageContext.lang}
          titleTemplate={`%s`}
          description={t(
            'Secure password manager app for Android. Store unlimited passwords, sync across devices, and protect your data with military-grade encryption. Free open-source solution.'
          )}
          keywords={[
            'password manager android',
            'android password app',
            'best free password manager for android',
            'android password vault',
            'secure password manager android',
            'password manager app android',
            'android password keeper',
            'mobile password manager android',
          ]}
        />

        <ResetCSS />
        <GlobalStyle />

        <ContentWrapper>
          <Sticky top={0} innerZ={9999} activeClass="sticky-nav-active">
            <DrawerProvider>
              <Navbar pageContext={pageContext} />
            </DrawerProvider>
          </Sticky>
          <BannerSection
            pageContext={pageContext}
            topText={t('Open-source password security for Android')}
            titleText={t('Google Android Password Manager')}
            descriptionText={t(
              'Best free password manager for Android. Secure Android password app with unlimited storage, biometric authentication, and cross-device sync.'
            )}
            primeLink={
              'https://play.google.com/store/apps/details?id=com.psono.psono'
            }
            primeText={t('INSTALL PSONO FOR ANDROID NOW')}
            downloadLink={`${pageContext.langPathPrefix}/download`}
            downloadText={t('VIEW ALL DOWNLOAD OPTIONS')}
          />
          <TextBoxSection
            description={t(
              'Professional password management for Android devices'
            )}
            text={t(
              'Psono delivers open-source password security designed specifically for Android users. Access unlimited password storage, real-time synchronization across all platforms, and enterprise-grade security features—all included in a permanently free tier with native Android integration and biometric support.'
            )}
          />
          <ComparisonTable
            features={comparisonFeatures.map((f) => ({
              ...f,
              chromeValue: f.androidValue,
            }))}
            browserName="Android"
            title={t(
              'Top reasons to choose Psono over Android native password manager'
            )}
          />
          <TextBoxSection
            description={t('Enhanced security beyond Android defaults')}
            text={t(
              "Android's built-in password features provide basic credential storage, but enterprise-grade security demands more robust protection. True password security requires cryptographic uniqueness and sufficient complexity—requirements that manual password creation rarely meets. Psono generates provably random passwords using cryptographic algorithms for each service, implements NaCl encryption locally before any storage occurs, and offers configurable multi-factor authentication for vault protection. The Android app delivers native autofill integration for rapid credential access while preserving encryption integrity during both storage operations and cross-device synchronization."
            )}
          />
          <WorkingProcessSection
            title={t('Why choose Psono as your Android password manager?')}
            description={t(
              'Key features that make Psono stand out for Android users'
            )}
            buttonUrl="/features-for-users"
            items={androidFeatures}
            pageContext={pageContext}
          />
          <TextBoxSection
            description={t('Android installation and setup')}
            text={t(
              'Deploying Psono on Android requires minimal setup time: Create your Psono account at psono.pw and complete email verification. Open the Google Play Store on your Android device and install Psono Password Manager. Launch the app and sign in with your credentials. Enable Psono as your Android Autofill service in device settings. Once active, Psono prompts you to store credentials securely as you log into apps. Subsequent logins enable instant autofill across all your Android apps and browsers. Your encrypted vault synchronizes continuously across Android phones, tablets, Chromebooks, desktop computers, and iOS devices.'
            )}
          />
          <InfoSection
            pageContext={pageContext}
            titleContent={t('Android App Security')}
            descriptionContent={t(
              "The Psono Android app implements industry-standard NaCl cryptography for client-side encryption, securing vault data before network transmission. Our open-source codebase receives continuous evaluation from the international security community, providing transparency Android users value. Biometric authentication adds an extra layer of security using Android's secure hardware. Whether protecting personal credentials or managing corporate authentication systems, Psono integrates proven encryption directly into your Android device workflow."
            )}
          />
          <FaqSection data={androidFaqData} />
          <Footer pageContext={pageContext} />
        </ContentWrapper>
      </Fragment>
    </ThemeProvider>
  );
};

GoogleAndroidPasswordManager.propTypes = {
  title: PropTypes.object,
  description: PropTypes.object,
};

GoogleAndroidPasswordManager.defaultProps = {
  title: {
    fontSize: ['24px', '32px', '40px', '42px', '46px'],
    fontWeight: '700',
    color: '#fff',
    letterSpacing: '-0.025em',
    mb: ['20px', '25px', '25px', '25px', '25px'],
    lineHeight: '1.2',
    textAlign: 'center',
  },
  description: {
    fontSize: ['15px', '16px', '16px', '16px', '16px'],
    color: '#fff',
    lineHeight: '1.75',
    mb: '0',
    textAlign: 'center',
  },
};

export default GoogleAndroidPasswordManager;
